/**
 * @biomax/plugin-one-health — zoonotic spillover risk integrator.
 *
 * v0.1 scope:
 *   - Per-region SpilloverRiskInputs: wildlife trade volume, livestock
 *     density, deforestation edge length, climate suitability for
 *     bat / rodent / primate reservoirs
 *   - spilloverRiskScore() — weighted sum into a 0..1 score
 *   - Animal sentinel surveillance signal stub (USGS dead-bird programs,
 *     OIE/WOAH reports)
 *
 * References:
 *   - Jones et al. 2008 Nature — Global trends in emerging infectious diseases
 *   - Allen et al. 2017 Nat Commun — Global hotspots and correlates of zoonotic-host EIDs
 *   - Carlson et al. 2022 Nature — Climate change increases cross-species viral transmission risk
 */

import type { PluginManifest } from '@biomax/types';

export interface SpilloverRiskInputs {
  regionId: string;
  /** Wildlife trade volume, USD millions/year. */
  wildlifeTradeVolumeUsdM: number;
  /** Livestock density, heads per km². */
  livestockDensityPerKm2: number;
  /** Annual deforestation rate, %/year. */
  deforestationRatePctPerYr: number;
  /** Bat / Pteropus species richness in region (proxy for paramyxovirus + filovirus risk). */
  batSpeciesRichness?: number;
  /** Climate suitability for vector + reservoir (0..1). */
  climateSuitability?: number;
  /** Surveillance capacity (0..1; 1 = good detection). */
  surveillanceCapacity?: number;
}

export interface SpilloverRiskOutput {
  regionId: string;
  /** Aggregate score in [0, 1] (1 = highest risk). */
  score: number;
  /** Per-driver breakdown for transparency. */
  contributions: {
    wildlifeTrade: number;
    livestockDensity: number;
    deforestation: number;
    bats: number;
    climate: number;
    surveillanceGap: number;
  };
}

/**
 * Compute a per-region spillover-risk score by weighting + clipping
 * each driver into [0, 1] and combining. Weights informed by Jones
 * 2008 + Allen 2017 + Carlson 2022; calibration is v0.4 work.
 */
export function spilloverRiskScore(inputs: SpilloverRiskInputs): SpilloverRiskOutput {
  const clip01 = (x: number): number => Math.max(0, Math.min(1, x));
  // Each contribution is a 0..1 score scaled by a weight.
  const contributions = {
    wildlifeTrade: clip01(inputs.wildlifeTradeVolumeUsdM / 1000) * 0.20,
    livestockDensity: clip01(inputs.livestockDensityPerKm2 / 200) * 0.15,
    deforestation: clip01(inputs.deforestationRatePctPerYr / 3) * 0.15,
    bats: clip01((inputs.batSpeciesRichness ?? 0) / 100) * 0.20,
    climate: (inputs.climateSuitability ?? 0.5) * 0.15,
    surveillanceGap: (1 - (inputs.surveillanceCapacity ?? 0.5)) * 0.15,
  };
  const score = Object.values(contributions).reduce((a, b) => a + b, 0);
  return { regionId: inputs.regionId, score, contributions };
}

/** Animal-sentinel surveillance signal types. */
export type AnimalSentinelSignal =
  | 'dead-bird-cluster'
  | 'wildlife-mass-die-off'
  | 'livestock-outbreak'
  | 'serosurvey-anomaly'
  | 'roadkill-spike';

export interface AnimalSentinelEvent {
  regionId: string;
  signalType: AnimalSentinelSignal;
  species: string;
  dateIso: string;
  /** Severity 1-5. */
  severity: 1 | 2 | 3 | 4 | 5;
  /** Source agency (USGS / OIE-WOAH / national vet authority). */
  source: string;
  note?: string;
}

/**
 * Citations for the load-bearing claims behind spilloverRiskScore. PMIDs
 * round-tripped via @biomax/plugin-literature-fetch on verifiedAt.
 * Re-run scripts/verify-references.ts to revalidate.
 */
export const ONE_HEALTH_REFERENCES = [
  {
    id: 'plowright-2017',
    label: 'Plowright et al. 2017 Nat Rev Microbiol — "Pathways to zoonotic spillover"',
    pmid: '28555073',
    doi: '10.1038/nrmicro.2017.45',
    appliesTo: 'spilloverRiskScore (cascade framework)',
    note: 'Foundational synthesis of the cascade from reservoir → human spillover: pathogen pressure × exposure × susceptibility × within-host dynamics. Each spilloverRiskScore driver maps to one stage of this cascade.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'allen-2017',
    label: 'Allen et al. 2017 Nat Commun — "Global hotspots and correlates of emerging zoonotic diseases"',
    pmid: '29066781',
    doi: '10.1038/s41467-017-00923-8',
    appliesTo: 'spilloverRiskScore (geographic weighting)',
    note: 'Mapping of zoonotic EID hotspots and correlates (mammal richness, livestock, land-use change). Backs the per-region weighting and the wildlifeTrade + livestockDensity + deforestation driver set.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'jones-2008',
    label: 'Jones et al. 2008 Nature — "Global trends in emerging infectious diseases"',
    pmid: '18288193',
    doi: '10.1038/nature06536',
    appliesTo: 'spilloverRiskScore (driver inventory)',
    note: 'Original global EID-trends paper. Quantified that 60% of EIDs are zoonotic and 71% of zoonotic EIDs originate in wildlife. Underwrites the driver list (wildlife trade, livestock density, deforestation, surveillance gap).',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'olival-2017',
    label: 'Olival et al. 2017 Nature — "Host and viral traits predict zoonotic spillover from mammals"',
    pmid: '28636590',
    doi: '10.1038/nature22975',
    appliesTo: 'spilloverRiskScore (host-trait weighting)',
    note: 'Phylogenetic + ecological correlates of spillover propensity per mammalian host. Backs the batSpeciesRichness driver and informs future per-host weight refinement.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'carlson-2022',
    label: 'Carlson et al. 2022 Nature — "Climate change increases cross-species viral transmission risk"',
    pmid: '35483403',
    doi: '10.1038/s41586-022-04788-w',
    appliesTo: 'spilloverRiskScore (climate suitability driver)',
    note: 'Projection of climate-driven range overlap → novel viral sharing among mammals. Backs the climateSuitability driver.',
    verifiedAt: '2026-05-28',
  },
] as const;

export const manifest: PluginManifest = {
  id: 'one-health', version: '0.1.0',
  engineCompat: '^0.5.0', kingdoms: '*', systems: [], components: [],
};
